export type PlayerStateRecord = Record<string, unknown>;

const GRID_FORMAT = "mattermaze-sparse-grid-v1";
const DEFAULT_CELL: PlayerStateRecord = {
  terrain: null,
  material: null,
  climb: null,
  interactive: null,
  marker: null,
  buildZone: null,
  deviceUid: null,
  deviceNo: null,
  deviceName: "",
  simpleChannel: null,
  logicMode: "advanced",
  triggerBehavior: "toggle",
  sequence: [],
};

interface StoredGrid {
  __matterMazeGrid: typeof GRID_FORMAT;
  rows: number;
  cols: number;
  /** JSON keeps row/cell arrays out of Firestore's unsupported nested-array shape. */
  data: string;
}

const jsonClone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const sameValue = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

function isGrid(value: unknown): value is PlayerStateRecord[][] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (row) =>
        Array.isArray(row) &&
        row.every((cell) => !!cell && typeof cell === "object" && !Array.isArray(cell)),
    )
  );
}

function encodeGrid(value: unknown): unknown {
  if (!isGrid(value)) return value;
  const rows = value.length;
  const cols = Math.max(0, ...value.map((row) => row.length));
  const changed: PlayerStateRecord[] = [];
  value.forEach((row, y) =>
    row.forEach((cell, x) => {
      const compact: PlayerStateRecord = { i: y * cols + x };
      for (const [key, item] of Object.entries(cell)) {
        if (item !== undefined && !sameValue(item, DEFAULT_CELL[key]))
          compact[key] = item;
      }
      if (Object.keys(compact).length > 1) changed.push(compact);
    }),
  );
  return {
    __matterMazeGrid: GRID_FORMAT,
    rows,
    cols,
    data: JSON.stringify(changed),
  } satisfies StoredGrid;
}

function decodeGrid(value: unknown): unknown {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    (value as Partial<StoredGrid>).__matterMazeGrid !== GRID_FORMAT
  )
    return value;
  const stored = value as StoredGrid;
  const rows = Math.max(1, Math.min(200, Math.floor(Number(stored.rows) || 30)));
  const cols = Math.max(1, Math.min(200, Math.floor(Number(stored.cols) || 20)));
  const grid = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => jsonClone(DEFAULT_CELL)),
  );
  try {
    const changed = JSON.parse(stored.data) as PlayerStateRecord[];
    if (!Array.isArray(changed)) return grid;
    for (const item of changed) {
      const index = Math.floor(Number(item?.i));
      if (!Number.isFinite(index) || index < 0 || index >= rows * cols) continue;
      const { i: _index, ...cell } = item;
      grid[Math.floor(index / cols)][index % cols] = {
        ...jsonClone(DEFAULT_CELL),
        ...cell,
      };
    }
  } catch {
    return grid;
  }
  return grid;
}

export function encodePlayerStateForStorage(state: PlayerStateRecord): PlayerStateRecord {
  const encoded = jsonClone(state);
  if ("homeMap" in encoded) encoded.homeMap = encodeGrid(encoded.homeMap);
  if (encoded.homeRooms && typeof encoded.homeRooms === "object" && !Array.isArray(encoded.homeRooms)) {
    for (const room of Object.values(encoded.homeRooms as Record<string, PlayerStateRecord>))
      if (room && typeof room === "object" && !Array.isArray(room) && "map" in room)
        room.map = encodeGrid(room.map);
  }
  return encoded;
}

export function decodePlayerStateFromStorage(state: PlayerStateRecord): PlayerStateRecord {
  const decoded = jsonClone(state);
  if ("homeMap" in decoded) decoded.homeMap = decodeGrid(decoded.homeMap);
  if (decoded.homeRooms && typeof decoded.homeRooms === "object" && !Array.isArray(decoded.homeRooms)) {
    for (const room of Object.values(decoded.homeRooms as Record<string, PlayerStateRecord>))
      if (room && typeof room === "object" && !Array.isArray(room) && "map" in room)
        room.map = decodeGrid(room.map);
  }
  return decoded;
}

